/**
 * PhishTrainer entity types.
 * All data lives in data/phishtrainer/*.json — mirror the host project's JSON DB pattern.
 */

import type { PhishPillarCode, PlayerSignal, PhishDifficulty } from './phish'

export type { PhishPillarCode, PhishDifficulty, PhishLetter, PlayerSignal, PhishFlag, PhishPillar, PhishFramework } from './phish'

// ─── PhishTrainer User ────────────────────────────────────────────────────
// Mirror of a RecipientFirewall recipient + PhishTrainer-specific enrichment.
// Never overwrites RF data; links via recipientId.

export type PtUserStatus = 'active' | 'paused' | 'opted_out'
/**
 * Derived learner onboarding/journey status. Title Case With Spaces per the
 * HFv3 enum convention (customer-facing) — sent verbatim by the backend.
 */
export type PtOnboardingStatus =
  | 'Not Invited'
  | 'Invited'
  | 'Invite Expired'
  | 'Joined'
  | 'In Progress'
  | 'Active'
  | 'Lapsed'
  | 'Dormant'
  | 'Paused'
  | 'Opted Out'
  | 'Deleted'
export type PtSeniority = 'IC' | 'Manager' | 'Director' | 'VP' | 'CXO'
export type PtRiskBand = 'low' | 'medium' | 'high' | 'critical'

export interface PhishTrainerUser {
  id: string
  recipientId: string
  email: string
  firstName: string
  lastName: string
  designation?: string
  department?: string
  location?: string
  country?: string
  managerEmail?: string
  tags: string[]
  groupIds: string[]
  company?: string
  seniorityLevel?: PtSeniority
  riskBand?: PtRiskBand
  enrolledProgramIds: string[]
  status: PtUserStatus
  /** Derived learner journey status (server-computed from invite + round signals). */
  onboardingStatus?: PtOnboardingStatus
  baselineCompletedAt?: string
  lastRoundAt?: string
  totalRounds: number
  totalPoints: number
  importedAt: string
  lastSyncedAt: string
  /**
   * True for users minted by the wrapper-.eml anonymous-play flow. These
   * never came from RF; keep them filtered out of import/sync screens.
   */
  isAnonymous?: boolean
  /** Human-entered display name captured from the anon-play entry card. */
  displayName?: string
  /** Attribution of how the user entered the system (e.g. 'eml-wrapper'). */
  createdVia?: string
  preferences?: {
    modeDefault?: 'guided' | 'expert'
  }
  stats?: {
    inboxCallStreak?: number
    inboxCallStreakLastAt?: string
    inboxCallStarsEarned?: number
    expertCompletions?: number
  }
}

// ─── Knowledge Profile ────────────────────────────────────────────────────

export type MasteryConfidence = 'unknown' | 'low' | 'medium' | 'high'

export interface SignalMastery {
  attempts: number
  correct: number
  ewmaAccuracy: number
  confidence: MasteryConfidence
  lastUpdatedAt: string
}

export interface KnowledgeProfile {
  userId: string
  baselineComplete: boolean
  signals: Record<PhishPillarCode, SignalMastery>
  subSignals: Record<string, SignalMastery>
  weakestSignals: PhishPillarCode[]
  weakestSubSignals: string[]
  strongestSignals: PhishPillarCode[]
  /**
   * Per-SIGNAL mastery keyed by the email cue the game actually tests
   * (PtPlayerSignal PascalCase: SenderName, SenderEmail, Subject, Greeting,
   * PsychManipulation, Link, Attachment, Spelling). Populated from every round's
   * answers — unlike subSignals (per-flag) which the live game never writes.
   */
  signalsBreakdown?: Record<
    string,
    { attempts: number; correct: number; accuracy: number }
  >
  overallScore: number
  lastRoundAt?: string
}

// ─── SafeAttack ───────────────────────────────────────────────────────────

export type SafeAttackSource =
  | 'global'
  | 'company'
  | 'real_attack'
  | 'needle'
  | 'spear'
  // Legacy aliases — treat as global/company in code
  | 'central_library'
  | 'company_library'

/** Is it actually phishing, legitimate, or borderline? */
export type SafeAttackNature = 'phishing' | 'legitimate' | 'ambiguous'

/**
 * Attack sophistication tier.
 * T0 = basic old-school (zero-knowledge test)
 * T1-T5 = increasing sophistication.
 * Maps to learner-facing difficulty: T0-T1→Easy, T2-T3→Medium, T4-T5→Hard
 */
export type AttackTier = 'T0' | 'T1' | 'T2' | 'T3' | 'T4' | 'T5'

/** When should the selection engine serve this? */
export type SafeAttackPurpose =
  | 'onboarding'
  | 'baseline'
  | 'general'
  | 'advanced'
  | 'seasonal'
  | 'compliance'

/** Configurable nature mix — what % of rounds should be phishing vs. legitimate vs. ambiguous */
export interface NatureMix {
  phishingPercent: number    // default 60
  legitimatePercent: number  // default 30
  ambiguousPercent: number   // default 10
}

export const DEFAULT_NATURE_MIX: NatureMix = {
  phishingPercent: 60,
  legitimatePercent: 30,
  ambiguousPercent: 10,
}

/** Maps attack tier → learner-facing difficulty label */
export function tierToDifficulty(tier: AttackTier): 'easy' | 'medium' | 'hard' {
  if (tier === 'T0' || tier === 'T1') return 'easy'
  if (tier === 'T2' || tier === 'T3') return 'medium'
  return 'hard'
}

export type SafeAttackStatus = 'draft' | 'active' | 'archived'
export type SafeAttackReviewStatus = 'pending' | 'approved' | 'rejected'

export interface SafeAttackLink {
  displayText: string
  href: string
  isMalicious: boolean
}

export interface SafeAttackAttachment {
  filename: string
  mimeType: string
  isMalicious: boolean
}

export interface PhishMarker {
  pillarCode: PhishPillarCode
  flagId?: string
  playerSignal: PlayerSignal
  severity: 'low' | 'medium' | 'high'
  explanation: string
}

// ─── Maker-Checker audit types ──────────────────────────────────────────

export type MarkerVerdict = 'correct' | 'wrong_flag' | 'wrong_signal' | 'wrong_severity' | 'fabricated'

export interface MarkerAudit {
  flagId: string
  verdict: MarkerVerdict
  correctedFlagId?: string
  correctedPlayerSignal?: PlayerSignal
  correctedSeverity?: 'low' | 'medium' | 'high'
  correctedExplanation?: string
  reason: string
}

export interface MakerCheckerMeta {
  makerSource: 'gemini' | 'heuristic'
  checkerScore: number
  checkerVerdict: 'pass' | 'corrected' | 'reject'
  checkerModel: string
  audits: MarkerAudit[]
  missedFlags: PhishMarker[]
  checkedAt: string
}

export interface SafeAttack {
  id: string
  source: SafeAttackSource
  capturedAt: string
  fromName: string
  fromEmail: string
  subject: string
  greeting: string
  bodyHtml: string
  /** Raw .eml source (when ingested via AttackFlipper) — preserved for debugging and full-source view */
  rawSource?: string
  links: SafeAttackLink[]
  attachments: SafeAttackAttachment[]
  phishMarkers: PhishMarker[]
  difficulty: 'easy' | 'medium' | 'hard' | 'expert'

  // ─── New classification axes ───────────────────────────────────────────
  /** Is this actually phishing, a legit decoy, or borderline? Defaults to 'phishing' for backwards compat. */
  nature: SafeAttackNature
  /** Attack sophistication tier (T0=zero-knowledge, T5=AI-crafted) */
  tier: AttackTier
  /** When should the engine serve this? */
  purpose: SafeAttackPurpose
  /** Freeform category tags — added on demand (e.g. 'credential_login', 'bec_wire_transfer') */
  categoryTags: string[]
  /** Seasonal period if purpose=seasonal (e.g. 'tax_season', 'holiday_shopping') */
  seasonalTag?: string
  /** Was this generated by AI? If so, which model. */
  generatedBy?: string

  // ─── Legacy field (kept for backwards compat) ──────────────────────────
  category?: string
  industryTags?: string[]
  geographyTags?: string[]
  roleTags?: string[]
  needleTargetUserId?: string
  spearTargetUserIds?: string[]
  spearTargetGroupId?: string
  sourceIncidentId?: string
  attackFlipperRunId?: string
  submittedByUserId?: string
  submittedAt?: string
  reviewStatus?: SafeAttackReviewStatus
  reviewedBy?: string
  reviewedAt?: string
  rejectionReason?: string
  /** Maker-checker audit trail for phishMarker quality */
  makerCheckerMeta?: MakerCheckerMeta
  status: SafeAttackStatus
  createdAt: string
  createdBy: string
  /** Non-fatal warnings emitted by the email-formatter pipeline (e.g. TOO_FEW_PARAGRAPHS). */
  formatterWarnings?: string[]

  // ─── Variety self-tagging (set by AI Gen Service when _enforceVariety is true) ──
  /** Primary psychological lever the variant leans on. */
  _variantLever?: string
  /** Sender persona archetype the variant uses. */
  _variantArchetype?: string
  /** Set by batchGenerate when a near-duplicate sibling exists in the same batch. */
  similarTo?: string | null
  /** Model-authored summary used for fingerprinting + admin review. */
  explanation?: string
}

// ─── Round ────────────────────────────────────────────────────────────────

export type RoundOutcome = 'passed' | 'failed' | 'partial'

export interface RoundAnswer {
  playerSignal: PlayerSignal
  userMarkedSuspicious: boolean
  isCorrect: boolean
  pointsAwarded: number
  timeMs: number
}

export interface PillarRollup {
  correct: number
  total: number
}

export interface Round {
  id: string
  userId: string
  safeAttackId: string
  programId?: string
  isBaseline: boolean
  startedAt: string
  completedAt?: string
  answers: RoundAnswer[]
  totalPoints: number
  correctCount: number
  totalCount: number
  retriesUsed: number
  outcome: RoundOutcome
  phishRollup: Record<PhishPillarCode, PillarRollup>
  /** Which scoring mode was active when this round was played. Defaults to 'guided' for legacy rounds. */
  mode?: 'guided' | 'expert'

  /** Expert mode phase reached. Omitted for Guided rounds. */
  expertPhase?: 'gate-only' | 'gate-then-hunt' | 'recovery-hunt'

  /** The Phase 1 overall call. Omitted for Guided rounds. */
  gateCall?: 'safe' | 'unsafe'

  /** Server-validated gate points (independent of per-element answers). */
  gatePoints?: number
}

// ─── Inbox Call ───────────────────────────────────────────────────────────

export type InboxCallChoice = 'safe' | 'unsafe'
export type InboxCallSource = 'eml' | 'web'

export interface InboxCall {
  id: string
  userId: string
  /** True when the call came from an anonymous .eml session before login. */
  anon: boolean
  /** Set when `anon` is true; cleared on merge into a real user. */
  sessionToken?: string
  attackId: string
  call: InboxCallChoice
  /** Snapshot of attack.nature at click time. */
  groundTruth: 'phishing' | 'legitimate' | 'ambiguous'
  correct: boolean
  pointsAwarded: number
  /** Source surface — currently only 'eml' (from training pack). */
  source: InboxCallSource
  /** ISO timestamp. */
  timestamp: string
}

// ─── Program ──────────────────────────────────────────────────────────────

export type ProgramStatus = 'draft' | 'active' | 'paused' | 'archived'
export type CurriculumMode = 'baseline_then_adaptive' | 'fully_adaptive' | 'fixed_sequence'
export type CadenceFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'custom'
export type DayOfWeek = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'

export interface ProgramCadence {
  frequency: CadenceFrequency
  customDays?: number
  sendDays?: DayOfWeek[]
  sendWindowStart: string
  sendWindowEnd: string
  timezone: string
  respectBusinessHours: boolean
}

export interface ProgramAdaptive {
  enabled: boolean
  weakUserMultiplier: number
  strongUserMultiplier: number
  masteryThresholdPercent: number
}

export interface ProgramNatureMix extends NatureMix {}

export interface Program {
  id: string
  name: string
  description?: string
  status: ProgramStatus
  rfGroupIds: string[]
  excludeRecipientIds?: string[]
  estimatedUserCount: number
  curriculumMode: CurriculumMode
  fixedSequenceSafeAttackIds?: string[]
  cadence: ProgramCadence
  adaptive: ProgramAdaptive
  /** Nature mix override — if not set, uses org-level defaults from settings */
  natureMix?: NatureMix
  reminderPolicyId: string
  startsAt?: string
  endsAt?: string
  nextSendAt?: string
  enrolledUserCount?: number
  completedBaselineCount?: number
  createdAt: string
  createdBy: string
  updatedAt: string
}

// ─── Reminders ────────────────────────────────────────────────────────────

export type ReminderChannel = 'email' | 'in_app' | 'both'
export type ReminderTrigger =
  | 'no_round_in_n_days'
  | 'failed_last_round'
  | 'weak_signal_detected'
  | 'baseline_pending'
  | 'streak_reward'

export interface ReminderTemplate {
  id: string
  name: string
  channel: ReminderChannel
  subject: string
  bodyMarkdown: string
  variables: string[]
}

export interface ReminderRule {
  id: string
  trigger: ReminderTrigger
  triggerParams: Record<string, unknown>
  /** Delivery channel for this rule's reminder. Copy comes from Notifications. */
  channel: ReminderChannel
  /** @deprecated Legacy reminder-template FK — copy now lives in Notifications. */
  templateId?: string
  cooldownHours: number
}

export interface ReminderPolicy {
  id: string
  name: string
  description?: string
  rules: ReminderRule[]
  isDefault: boolean
}

export type ReminderStatus = 'pending' | 'sent' | 'cancelled' | 'failed'

export interface ReminderQueueItem {
  id: string
  userId: string
  programId?: string
  ruleId: string
  templateId: string
  scheduledFor: string
  status: ReminderStatus
  payload: { subject: string; body: string }
  generatedAt: string
  sentAt?: string
}

// ─── Round Analysis (Gemini) ──────────────────────────────────────────────

export interface PerFlagAssessment {
  flagId: string
  flagCode: string
  expectedDetection: boolean
  userDetected: boolean
  nuanceScore: number
  reasoning: string
}

export interface PerPillarRollupAnalysis {
  pillarCode: PhishPillarCode
  masteryDelta: number
  confidenceShift: 'up' | 'down' | 'stable'
}

export interface RoundAnalysis {
  roundId: string
  perFlagAssessment: PerFlagAssessment[]
  perPillarRollup: PerPillarRollupAnalysis[]
  coachingMessage: string
  suggestedNextFocus: string[]
  riskFlags: string[]
  generatedAt: string
  modelVersion: string
  promptTokens: number
  outputTokens: number
  fallback?: boolean
}

// ─── Round Report (full per-round transparency) ──────────────────────────
// See docs/phishtrainer-todo.md §1 — the durable, self-contained record of
// what happened during a round: what was selected, why, what the player saw,
// how they answered, what the knowledge engine did, and (optionally) what
// Gemini said. `emailSnapshot` is a deep copy at serve-time so retroactive
// SafeAttack edits never corrupt historical reports.

export type SelectionStrategy = 'baseline' | 'weakness-targeted' | 'exploration' | 'forced' | 'assigned' | 'fallback'

export interface SelectionCandidate {
  safeAttackId: string
  score: number
  reasons: string[]
}

export interface SelectionTelemetry {
  strategy: SelectionStrategy
  chosenSafeAttackId: string
  chosenReason: string
  sourcePriorityHit?: SafeAttackSource | 'forced'
  weaknessTargeted?: PhishPillarCode
  isExploration?: boolean
  difficultyBand?: 'Easy' | 'Medium' | 'Hard' | 'Mixed'
  candidatePool: SelectionCandidate[]
  capturedAt: string
}

export interface RoundReportEmailSnapshot {
  subject: string
  senderName: string
  senderEmail: string
  greeting: string
  bodyHtml: string
  difficulty: SafeAttack['difficulty']
  source: SafeAttackSource
  phishMarkers: Array<{
    playerSignal: PlayerSignal
    flagId?: string
    pillarCode: PhishPillarCode
    expectedAnswer: 'unsafe'
    markerText: string
    severity: 'low' | 'medium' | 'high'
    difficulty?: PhishDifficulty
  }>
}

export interface RoundReportAnswer {
  playerSignal: PlayerSignal
  userMarkedSuspicious: boolean
  isCorrect: boolean
  pointsAwarded: number
  timeMs: number
  markerHits: string[]       // flagIds the player correctly flagged
  markerMisses: string[]     // flagIds present on this signal but player marked safe
  falsePositiveSignals: string[] // playerSignals user marked unsafe that had no marker
}

export interface ProfilePillarSnapshot {
  ewma: number
  attempts: number
  confidence: MasteryConfidence
}

export interface ProfileFlagSnapshot {
  ewma: number
  attempts: number
}

export interface KnowledgeSampleApplied {
  pillar: PhishPillarCode
  sample: 0 | 1
  source: 'binary-answer' | 'gemini-nuance'
  fromAnswer?: PlayerSignal
}

export interface KnowledgeDeltaPayload {
  pillarsBefore: Record<PhishPillarCode, ProfilePillarSnapshot>
  pillarsAfter: Record<PhishPillarCode, ProfilePillarSnapshot>
  flagsBefore: Record<string, ProfileFlagSnapshot>
  flagsAfter: Record<string, ProfileFlagSnapshot>
  samplesApplied: KnowledgeSampleApplied[]
  overallScoreBefore: number
  overallScoreAfter: number
}

export interface RoundReportScoring {
  correctCount: number
  totalCount: number
  percentage: number
  totalPoints: number
  retriesUsed: number
  outcome: RoundOutcome
  streakAfter?: number
}

export interface RoundReport {
  id: string
  roundId: string
  userId: string
  safeAttackId: string
  campaignId?: string
  programId?: string
  createdAt: string

  selection: SelectionTelemetry
  emailSnapshot: RoundReportEmailSnapshot
  answers: RoundReportAnswer[]
  scoring: RoundReportScoring
  knowledgeDelta: KnowledgeDeltaPayload
  geminiAnalysis?: {
    invokedAt: string
    model: string
    perFlagAssessment: PerFlagAssessment[]
    perPillarRollup: PerPillarRollupAnalysis[]
    coachingMessage: string
    suggestedNextFocus: string[]
    fallback: boolean
    promptTokens?: number
    outputTokens?: number
  }

  engineVersion: string
  frameworkVersion: string
  backfilled?: boolean
}

// ─── Audit Log ────────────────────────────────────────────────────────────

export interface PtAuditEntry {
  id: string
  action: string
  entityType: string
  entityId: string
  entityName?: string
  description: string
  performedBy: string
  timestamp: string
  metadata?: Record<string, unknown>
}

// ─── Generation History ──────────────────────────────────────────────

export type GenerationMode = 'sentence' | 'chat' | 'form' | 'api'
export type GenerationStatus = 'success' | 'error'

export interface GenerationRecord {
  id: string
  /** Which UI mode triggered this */
  mode: GenerationMode
  /** The request that was sent to the engine */
  request: {
    nature: SafeAttackNature
    tier: AttackTier
    purpose: SafeAttackPurpose
    categoryTags: string[]
    targetContext: {
      industry?: string
      department?: string
      seniority?: string
      geography?: string
      companyName?: string
      seasonalContext?: string
    }
    brandToImpersonate?: string
    additionalInstructions?: string
  }
  /** Outcome */
  status: GenerationStatus
  /** ID of the SafeAttack created (if success) */
  safeAttackId?: string
  /** Subject of the generated email (if success) */
  subject?: string
  /** Number of phish markers generated */
  markerCount?: number
  /** Model used */
  modelUsed: string
  /** Error message (if error) */
  errorMessage?: string
  /** Duration in ms */
  durationMs: number
  /** Who triggered it */
  performedBy: string
  /** For chat mode — the user's latest message */
  chatPrompt?: string
  /** For chat mode — full conversation history leading to this generation */
  chatHistory?: { role: 'user' | 'assistant'; content: string }[]
  /** For sentence mode — the composed sentence that was shown in the UI */
  sentenceText?: string
  /** Human-readable summary of what the user asked — works across all modes */
  inputSummary?: string
  /** Batch ID — links multiple generations from a single batch request */
  batchId?: string
  createdAt: string
}

// ─── Settings ─────────────────────────────────────────────────────────────

export type MobileCardStyle = 'inline' | 'overlay'

export interface PhishTrainerSettings {
  org: {
    name: string
    industry?: string
    geography?: string
  }
  branding: {
    /** Path to org logo file relative to public/ (e.g. "brand/uploads/logo.png") */
    logoPath?: string
    /** Primary brand color for email CTA buttons, accents */
    primaryColor: string
    /** Footer text shown in emails (e.g. "a Wipro CyberDefence Initiative") */
    footerText?: string
    /**
     * Monotonically-increasing version that bumps on every settings save.
     * Mixed into the screenshot cache hash so any branding change lazily
     * invalidates every attack's cached PNG without mass regen.
     */
    brandingVersion?: number
  }
  /**
   * Absolute base URL used when building PhishTrainer deep-links (e.g. the
   * "Play in PhishTrainer" button in wrapper .eml files). When empty, the
   * server falls back to the request's own origin at generation time.
   */
  trainingBaseUrl?: string
  defaults: {
    cadenceFrequency: CadenceFrequency
    retryLimit: number
    explorationPercent: number
    baselineRoundCount: number
    safeAttackCooldownDays: number
    /** Org-wide default nature mix — programs can override */
    natureMix: NatureMix
  }
  gameplay: {
    enableWhyFeature: boolean
    mobileCardStyle: MobileCardStyle
    mode: import('../scoring-config').ModePolicy
    scoring: import('../scoring-config').ScoringConfig
    trainingPack: import('../scoring-config').TrainingPackConfig
  }
  ai: {
    analysisEnabled: boolean
    coachingEnabled: boolean
    fallbackOnError: boolean
  }
  updatedAt: string
  updatedBy: string
}

export * from './gamification'
export type { PtMode, ModeScoring, ModePolicy, ScoringConfig, InboxCallScoring, TrainingPackConfig } from '../scoring-config'
